import type { CommitmentSummary, CreditPoolWithUnits } from '@vy/api-definition';
import { formatPrice } from '@vy/services';
import type { Currency } from '@vy/types';
import { COMMITMENT_TYPES, DEFAULT_CURRENCY } from '@vy/types';
import { formatNumber, isNotNullOrUndefined } from '@vy/utils';
import { useMemo, useState } from 'react';
import type { SelectItem } from '../../../../../../components/new-design/molecules';
import { VyDialog } from '../../../../../../components/new-design/feedback/VyDialog';
import { CreditsTracker } from '../../../../../customers/CustomersPage/customers-page-components/CustomerOverview/customer-overview-components/CustomerActiveContracts/ContractUsageDialog/components/CreditsTracker/CreditsTracker';
import type { CommitmentTotals } from '../../../../../customers/CustomersPage/customers-page-components/CustomerOverview/customer-overview-components/CustomerActiveContracts/ContractUsageDialog/ContractUsageDialogContent';
import { ContractUsageDialogContent } from '../../../../../customers/CustomersPage/customers-page-components/CustomerOverview/customer-overview-components/CustomerActiveContracts/ContractUsageDialog/ContractUsageDialogContent';
import {
  PRODUCT_USAGE_BREAKDOWN_DATE_RESOLUTIONS,
  type ProductUsageBreakdownDateResolution,
} from '../../../../../customers/CustomersPage/customers-page-components/CustomerOverview/customer-overview-components/ViewProducts/ProductUsageBreakdownChart/product-usage-breakdown-chart-types/product-usage-breakdown-date-resolution';
import type { UsageDataItem } from '../../../../hooks/use-customer-portal-chart-data-utils';
import { creditPoolToTrackerTotals } from '../../utils/credit-pool-summary-to-tracker-totals';
import { mapUsageDataToUsageByDateBreakdown } from './map-usage-data-to-usage-by-date-breakdown';

type UsageBreakdownModalProps = {
  open: boolean;
  onClose: () => void;
  monthlyUsageData: UsageDataItem[];
  commitmentSummaries?: CommitmentSummary[];
  currency?: Currency;
  creditPool?: CreditPoolWithUnits;
};

export const UsageBreakdownModal: React.FC<UsageBreakdownModalProps> = ({
  open,
  onClose,
  monthlyUsageData,
  commitmentSummaries,
  currency,
  creditPool,
}) => {
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [dateResolution, setDateResolution] = useState<ProductUsageBreakdownDateResolution>(
    PRODUCT_USAGE_BREAKDOWN_DATE_RESOLUTIONS.MONTH,
  );
  const usageByDateBreakdowns = useMemo(
    () => mapUsageDataToUsageByDateBreakdown(monthlyUsageData),
    [monthlyUsageData],
  );

  const usageProducts = useMemo<SelectItem[]>(() => {
    const names = Array.from(new Set(monthlyUsageData.map((item) => item.productName)));
    return names.map((name) => ({ value: name, label: name }));
  }, [monthlyUsageData]);

  const commitmentTotals = useMemo<CommitmentTotals | undefined>(() => {
    if (!commitmentSummaries || commitmentSummaries.length === 0) {
      return undefined;
    }

    const resolvedCurrency = currency ?? DEFAULT_CURRENCY;
    const result: CommitmentTotals = {};

    for (const summary of commitmentSummaries) {
      if (summary.type === COMMITMENT_TYPES.Units) {
        result.units = { amount: summary.amount, formatted: formatNumber(summary.amount) };
      } else if (summary.type === COMMITMENT_TYPES.Price) {
        result.price = {
          amount: summary.amount,
          formatted: formatPrice(summary.amount, resolvedCurrency),
        };
      }
    }

    return isNotNullOrUndefined(result.units) || isNotNullOrUndefined(result.price) ? result : undefined;
  }, [commitmentSummaries, currency]);

  const creditPoolTotals = useMemo(() => {
    if (!creditPool) {
      return null;
    }

    return creditPoolToTrackerTotals(creditPool);
  }, [creditPool]);

  const showCreditsTracker = isNotNullOrUndefined(creditPool) && isNotNullOrUndefined(creditPoolTotals);

  return (
    <VyDialog open={open} onClose={onClose} label="Usage breakdown" disableDividers maxWidth="md" fullWidth>
      <ContractUsageDialogContent
        isLoading={false}
        usageByDateBreakdowns={usageByDateBreakdowns}
        usageProducts={usageProducts}
        dateResolution={dateResolution}
        onDateResolutionChange={setDateResolution}
        commitmentTotals={commitmentTotals}
        currency={currency}
        selectedProductIds={selectedProductIds}
        onSelectedProductsChange={setSelectedProductIds}
        creditsSlot={
          showCreditsTracker ? (
            <CreditsTracker
              totalCredits={creditPoolTotals.totalCredits}
              usedCredits={creditPoolTotals.usedCredits}
              resetDate={creditPool.closestEntryExpiryDate}
              schedule={creditPool.initialGrantSchedule}
              currency={currency}
              creditKind={creditPool.creditKind}
              units={creditPool.units}
            />
          ) : undefined
        }
      />
    </VyDialog>
  );
};
